import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Plus,
} from 'lucide-react';

import {
  getAvailableSetupContractors,
  createContractorSetupDraft,
  updateContractorSetupDraft,
  getContractorSetupDraft,
  getContractorWaiverPreview,
} from '@/api/contractorOnboardingAdminApi';

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';

import {
  Button,
} from '@/components/ui/button';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Input,
} from '@/components/ui/input';

import {
  Label,
} from '@/components/ui/label';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  Textarea,
} from '@/components/ui/textarea';


const emptyForm = {
  source_contractor_id: '',
  legal_name: '',
  trading_name: '',
  abn: '',
  master_licence: '',
  master_licence_expiry: '',
  display_name: '',
  support_email: '',
  support_phone: '',
  waiver_title:
    'Subcontractor Employee Acknowledgement and Indemnity',
  waiver_mode: 'standard_bsms',
  custom_waiver_text: '',
};


export default function ContractorSetupWizard() {
  const [
    available,
    setAvailable,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    dialogOpen,
    setDialogOpen,
  ] = useState(false);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    form,
    setForm,
  ] = useState(emptyForm);

  const [
    createdDraft,
    setCreatedDraft,
  ] = useState(null);

  const [
    editingDraftId,
    setEditingDraftId,
  ] = useState(null);

  const [
    preview,
    setPreview,
  ] = useState(null);

  const [
    error,
    setError,
  ] = useState('');

  const loadContractors = async () => {
    setLoading(true);
    setError('');

    try {
      const result =
        await getAvailableSetupContractors();

      setAvailable(
        result?.contractors || []
      );

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load Rapid X contractors'
      );

    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      loadContractors();
    },
    []
  );

  const selectable = useMemo(
    () =>
      available.filter(
        (item) =>
          !item.already_configured
      ),
    [available]
  );

  const selectedSource = useMemo(
    () =>
      available.find(
        (item) =>
          item.source_contractor_id
          === form.source_contractor_id
      ),
    [
      available,
      form.source_contractor_id,
    ]
  );

  const updateField = (
    key,
    value
  ) => {
    setForm(
      (current) => ({
        ...current,
        [key]: value,
      })
    );
  };

  const selectContractor = (
    sourceId
  ) => {
    const contractor =
      available.find(
        (item) =>
          item.source_contractor_id
          === sourceId
      );

    if (!contractor) {
      return;
    }

    const legalName =
      contractor.legal_name
      || contractor.company_name
      || '';

    const tradingName =
      contractor.trading_name
      || contractor.company_name
      || legalName;

    setForm({
      ...emptyForm,
      source_contractor_id:
        sourceId,
      legal_name:
        legalName,
      trading_name:
        tradingName,
      abn:
        contractor.abn || '',
      display_name:
        tradingName || legalName,
      support_email:
        contractor.email || '',
      support_phone:
        contractor.phone || '',
    });

    setCreatedDraft(null);
    setPreview(null);
    setError('');
  };

  const openWizard = () => {
    setForm(emptyForm);
    setCreatedDraft(null);
    setEditingDraftId(null);
    setPreview(null);
    setError('');
    setDialogOpen(true);
  };

  const openExistingDraft = async (
    contractor
  ) => {
    const contractorId =
      contractor.onboarding_contractor_id;

    if (!contractorId) {
      setError(
        'Setup draft identifier is unavailable.'
      );
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result =
        await getContractorSetupDraft(
          contractorId
        );

      const draft = result?.contractor;

      if (!draft?.id) {
        throw new Error(
          'Rapid X did not return the setup draft.'
        );
      }

      const waiver =
        draft.waiver_configuration || {};

      setEditingDraftId(draft.id);
      setCreatedDraft(null);
      setPreview(null);

      setForm({
        ...emptyForm,
        source_contractor_id:
          draft.source_contractor_id || '',
        legal_name:
          draft.legal_name || '',
        trading_name:
          draft.trading_name || '',
        abn:
          draft.abn || '',
        master_licence:
          draft.master_licence || '',
        master_licence_expiry:
          draft.master_licence_expiry || '',
        display_name:
          draft.display_name || '',
        support_email:
          draft.support_email || '',
        support_phone:
          draft.support_phone || '',
        waiver_title:
          waiver.title
          || emptyForm.waiver_title,
        waiver_mode:
          waiver.mode
          || 'standard_bsms',
        custom_waiver_text:
          waiver.custom_text || '',
      });

      setDialogOpen(true);

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to open setup draft'
      );

    } finally {
      setSubmitting(false);
    }
  };

  const createDraft = async () => {
    if (!form.source_contractor_id) {
      setError(
        'Select a Rapid X contractor.'
      );
      return;
    }

    if (!form.legal_name.trim()) {
      setError(
        'Legal entity name is required.'
      );
      return;
    }

    if (
      form.waiver_mode === 'custom'
      && !form.custom_waiver_text.trim()
    ) {
      setError(
        'Enter the custom waiver text.'
      );
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...form,
        master_licence:
          form.master_licence || null,
        master_licence_expiry:
          form.master_licence_expiry || null,
        custom_waiver_text:
          form.waiver_mode === 'custom'
            ? form.custom_waiver_text
            : null,
      };

      const result =
        editingDraftId
          ? await updateContractorSetupDraft(
              editingDraftId,
              payload
            )
          : await createContractorSetupDraft(
              payload
            );

      const contractor =
        result?.contractor;

      if (!contractor?.id) {
        throw new Error(
          'Rapid X did not return a setup draft.'
        );
      }

      setCreatedDraft(contractor);

      const previewResult =
        await getContractorWaiverPreview(
          contractor.id
        );

      setPreview(previewResult);

      await loadContractors();

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to create setup draft'
      );

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>
              Contractor Onboarding Setup
            </CardTitle>

            <CardDescription>
              Select an existing Rapid X contractor,
              populate its onboarding profile and
              preview its waiver.
            </CardDescription>
          </div>

          <Button
            onClick={openWizard}
            disabled={
              loading
              || selectable.length === 0
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Contractor
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && !dialogOpen && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                Setup error
              </AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-sm text-muted-foreground">
              Loading Rapid X contractors…
            </p>
          ) : (
            <div className="grid gap-3">
              {available.map(
                (contractor) => (
                  <div
                    key={
                      contractor.source_contractor_id
                    }
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                  >
                    <div>
                      <p className="font-medium">
                        {
                          contractor.trading_name
                          || contractor.company_name
                        }
                      </p>

                      <p className="text-sm text-muted-foreground">
                        {contractor.legal_name}
                        {
                          contractor.abn
                            ? ` — ABN ${contractor.abn}`
                            : ''
                        }
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={
                          contractor.already_configured
                            ? 'rounded border border-emerald-700 bg-emerald-950/40 px-2 py-1 text-xs text-emerald-400'
                            : 'rounded border px-2 py-1 text-xs text-muted-foreground'
                        }
                      >
                        {
                          contractor.already_configured
                            ? contractor.setup_status
                            : 'Not configured'
                        }
                      </span>

                      {
                        contractor.setup_status === 'draft'
                        && contractor.onboarding_contractor_id
                        && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={
                              () =>
                                openExistingDraft(
                                  contractor
                                )
                            }
                          >
                            Manage Draft
                          </Button>
                        )
                      }
                    </div>
                  </div>
                )
              )}

              {available.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No Rapid X contractors are available.
                </p>
              )}
            </div>
          )}

          <Alert>
            <FileText className="h-4 w-4" />
            <AlertTitle>
              Draft-only safety
            </AlertTitle>
            <AlertDescription>
              Creating a setup draft does not enable
              onboarding or send an invitation.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={
          (open) => {
            if (!submitting) {
              setDialogOpen(open);
            }
          }
        }
      >
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Contractor to Onboarding
            </DialogTitle>

            <DialogDescription>
              {
                editingDraftId
                  ? 'Review and update this contractor setup draft.'
                  : (
                    'Details are populated from the existing '
                    + 'Rapid X contractor record and can be '
                    + 'completed before saving.'
                  )
              }
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                Unable to continue
              </AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {!createdDraft ? (
            <div className="grid gap-5 py-2">
              <div className="grid gap-2">
                <Label>
                  Existing Rapid X contractor
                </Label>

                <Select
                  value={
                    form.source_contractor_id
                  }
                  onValueChange={
                    selectContractor
                  }
                  disabled={
                    Boolean(editingDraftId)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contractor" />
                  </SelectTrigger>

                  <SelectContent>
                    {selectable.map(
                      (contractor) => (
                        <SelectItem
                          key={
                            contractor.source_contractor_id
                          }
                          value={
                            contractor.source_contractor_id
                          }
                        >
                          {
                            contractor.trading_name
                            || contractor.company_name
                          }
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              {selectedSource && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label>
                      Legal entity name
                    </Label>
                    <Input
                      value={form.legal_name}
                      onChange={
                        (event) =>
                          updateField(
                            'legal_name',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Trading/display name
                    </Label>
                    <Input
                      value={form.trading_name}
                      onChange={
                        (event) => {
                          updateField(
                            'trading_name',
                            event.target.value
                          );
                          updateField(
                            'display_name',
                            event.target.value
                          );
                        }
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>ABN</Label>
                    <Input
                      value={form.abn}
                      onChange={
                        (event) =>
                          updateField(
                            'abn',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Master licence
                    </Label>
                    <Input
                      value={
                        form.master_licence
                      }
                      onChange={
                        (event) =>
                          updateField(
                            'master_licence',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Master licence expiry
                    </Label>

                    <Input
                      type="date"
                      value={
                        form.master_licence_expiry
                      }
                      onChange={
                        (event) =>
                          updateField(
                            'master_licence_expiry',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Support email
                    </Label>
                    <Input
                      type="email"
                      value={
                        form.support_email
                      }
                      onChange={
                        (event) =>
                          updateField(
                            'support_email',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Support phone
                    </Label>
                    <Input
                      value={
                        form.support_phone
                      }
                      onChange={
                        (event) =>
                          updateField(
                            'support_phone',
                            event.target.value
                          )
                      }
                    />
                  </div>
                </div>
              )}

              {selectedSource && (
                <>
                  <div className="grid gap-2">
                    <Label>
                      Waiver configuration
                    </Label>

                    <Select
                      value={
                        form.waiver_mode
                      }
                      onValueChange={
                        (value) =>
                          updateField(
                            'waiver_mode',
                            value
                          )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="standard_bsms">
                          Standard BSMS subcontractor waiver
                        </SelectItem>

                        <SelectItem value="custom">
                          Contractor-specific waiver
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>
                      Waiver title
                    </Label>

                    <Input
                      value={
                        form.waiver_title
                      }
                      onChange={
                        (event) =>
                          updateField(
                            'waiver_title',
                            event.target.value
                          )
                      }
                    />
                  </div>

                  {form.waiver_mode === 'custom' && (
                    <div className="grid gap-2">
                      <Label>
                        Custom waiver text
                      </Label>

                      <Textarea
                        className="min-h-[280px] font-mono text-sm"
                        value={
                          form.custom_waiver_text
                        }
                        onChange={
                          (event) =>
                            updateField(
                              'custom_waiver_text',
                              event.target.value
                            )
                        }
                        placeholder={
                          'Use placeholders such as:\n'
                          + '{{candidate_full_name}}\n'
                          + '{{contractor_legal_name}}\n'
                          + '{{contractor_trading_name}}\n'
                          + '{{contractor_abn}}\n'
                          + '{{principal_name}}'
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="grid gap-4 py-2">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>
                  Draft created
                </AlertTitle>
                <AlertDescription>
                  {
                    createdDraft.display_name
                  } is saved as a draft. Onboarding
                  and delivery remain disabled.
                </AlertDescription>
              </Alert>

              <div className="rounded-lg border p-4">
                <p className="font-medium">
                  Waiver preview
                </p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Version: DRAFT
                </p>

                <pre className="mt-4 max-h-[430px] overflow-auto whitespace-pre-wrap rounded bg-muted p-4 text-sm">
                  {
                    preview?.waiver?.text
                    || 'Preview unavailable'
                  }
                </pre>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={
                () => setDialogOpen(false)
              }
              disabled={submitting}
            >
              {
                createdDraft
                  ? 'Close'
                  : 'Cancel'
              }
            </Button>

            {!createdDraft && (
              <Button
                onClick={createDraft}
                disabled={
                  submitting
                  || !form.source_contractor_id
                }
              >
                {
                  submitting
                    ? (
                      editingDraftId
                        ? 'Saving changes…'
                        : 'Creating draft…'
                    )
                    : (
                      editingDraftId
                        ? 'Save Changes & Preview'
                        : 'Save Draft & Preview'
                    )
                }
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
